import { createContext, useContext, useState } from "react";

// Precios base de los planes (en reales). La conversion es aproximada y se muestra con "~"/"≈".
export const PLAN_PRICE_BRL = [1400, 2600, 4000];
export const EXTRA_DAY_BRL = [150, 200, 300];
export const CHANGE_FEE_BRL = 200;
export const URGENCY_FEE_BRL = 400;

// Tasas fijas aproximadas (editar aqui cuando cambien): cuantos reales vale 1 unidad.
const BRL_PER = { USD: 5.6, EUR: 6.15 };
const SYMBOL = { USD: "US$", EUR: "€" };
const EURO_REGIONS = ["AT", "BE", "CY", "DE", "EE", "ES", "FI", "FR", "GR", "HR", "IE", "IT", "LT", "LU", "LV", "MT", "NL", "PT", "SI", "SK", "EU", "AD", "MC", "SM", "VA", "ME", "XK"];

function detectCurrency() {
  try {
    const region = ((navigator.language || "").split("-")[1] || "").toUpperCase();
    if (region === "BR") return "BRL";
    if (EURO_REGIONS.includes(region)) return "EUR";
    return "USD";
  } catch {
    return "BRL";
  }
}

const CurrencyContext = createContext(null);

const brlFmt = (v) => String(v).replace(/\B(?=(\d{3})+(?!\d))/g, ".");

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState(() => {
    try {
      const saved = localStorage.getItem("wyd_currency");
      if (["BRL", "USD", "EUR"].includes(saved)) return saved;
    } catch {}
    return detectCurrency();
  });

  const setCurrency = (c) => {
    if (!["BRL", "USD", "EUR"].includes(c)) return;
    try {
      localStorage.setItem("wyd_currency", c);
    } catch {}
    setCurrencyState(c);
  };

  const convert = (brl) => {
    const raw = brl / BRL_PER[currency];
    return raw >= 100 ? Math.round(raw / 5) * 5 : Math.round(raw);
  };

  // Precio principal: "R$ 1.400" | "~US$ 250" | "~€ 230"
  const fmtPrice = (brl) => {
    if (currency === "BRL") return `R$ ${brlFmt(brl)}`;
    return `~${SYMBOL[currency]} ${convert(brl).toLocaleString("en-US")}`;
  };

  // Valor secundario dentro de una frase: "+R$ 150" | "≈US$ 27" | "≈€ 24"
  const fmtAddon = (brl) => {
    if (currency === "BRL") return `+R$ ${brlFmt(brl)}`;
    return `≈${SYMBOL[currency]} ${convert(brl).toLocaleString("en-US")}`;
  };

  // Precio de una salida grupal: viene como texto desde el admin ("USD 3.490", "EUR 2.900", "R$ 18.000").
  // Se convierte a la moneda elegida en el selector. Si no se puede parsear, se muestra tal cual.
  const fmtTripPrice = (str) => {
    if (!str) return str;
    const m = String(str).match(/(US\$|USD|€|EUR|R\$|BRL)\s*([\d.,]+)/i);
    if (!m) return str;
    const srcCur = /usd|US\$/i.test(m[1]) ? "USD" : /eur|€/i.test(m[1]) ? "EUR" : "BRL";
    const num = parseFloat(m[2].replace(/\./g, "").replace(",", "."));
    if (isNaN(num)) return str;
    const brl = srcCur === "BRL" ? num : num * BRL_PER[srcCur];
    if (currency === srcCur) return str; // misma moneda: respetar el texto original del admin
    return fmtPrice(Math.round(brl));
  };

  return <CurrencyContext.Provider value={{ currency, setCurrency, fmtPrice, fmtAddon, fmtTripPrice }}>{children}</CurrencyContext.Provider>;
}

export const useCurrency = () => useContext(CurrencyContext);
